import React, { useState, ChangeEvent, FormEvent, useEffect } from 'react';
import { getCookie } from '../../utils/utils';

interface FormData {
  dialoutNumber: string;
  detectVoicemail: boolean;
  callId: string;
  clientName: string;
}

const CortexDialOut: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    dialoutNumber: '18777791003',
    detectVoicemail: false,
    callId: 'a6512da6-dbf0-4cce-af66-e16f41ca3578',
    clientName: 'default'
  });
  const [response, setResponse] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [isWarmedUp, setIsWarmedUp] = useState<boolean>(false);
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const [warmUpError, setWarmUpError] = useState<string | null>(null);
  const [isWarmingUp, setIsWarmingUp] = useState<boolean>(false);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (isWarmedUp && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000);
    } else if (timeLeft === 0) {
      setIsWarmedUp(false);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [isWarmedUp, timeLeft]);

  const handleWarmUp = async () => {
    setWarmUpError(null);
    setIsWarmingUp(true);
    try {
      const response = await fetch('https://api.cortex.cerebrium.ai/v4/p-151ed52d/cerebrium-orpheus-endpoint/tts/pipecat/stream', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          text: "Warming up instance",
          voice: "tara",
          sample_rate: 8000
        })
      });

      if (!response.ok) {
        throw new Error('Warm-up request failed');
      }

      // Get the response as a readable stream
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('Failed to get response stream');
      }

      // Read the stream until it's complete
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }
        // Process the chunk if needed
        // value is a Uint8Array containing the chunk data
      }

      // Only set warmed up after stream is complete
      setIsWarmedUp(true);
      setTimeLeft(60);
    } catch (err) {
      setWarmUpError(err instanceof Error ? err.message : 'Failed to warm up instance');
    } finally {
      setIsWarmingUp(false);
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setResponse(null);

    if (!isWarmedUp) {
      setError('Please warm up the instance before making a call');
      return;
    }

    const token = getCookie('Authorized');

    try {
      const response = await fetch('https://api.cortex.cerebrium.ai/v4/p-151ed52d/sophia-assist-dev-orpheus/daily_start_bot', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          dialoutNumber: formData.dialoutNumber.startsWith('+') ? formData.dialoutNumber : `+${formData.dialoutNumber}`
        })
      });

      const data = await response.json();
      setResponse(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const formWrapperStyle: React.CSSProperties = {
    fontFamily: 'Arial, sans-serif',
    backgroundColor: '#f4f4f4',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    padding: '20px',
    color: '#333'
  };

  const formStyle: React.CSSProperties = {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '30px',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
    width: '100%',
    maxWidth: '500px'
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px',
    marginTop: '8px',
    marginBottom: '20px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '16px'
  };

  const labelStyle: React.CSSProperties = {
    fontSize: '16px',
    color: '#34495e',
    marginBottom: '8px',
    display: 'block'
  };

  const buttonStyle: React.CSSProperties = {
    backgroundColor: '#3498db',
    color: 'white',
    border: 'none',
    padding: '12px 20px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '16px',
    width: '100%',
    transition: 'background-color 0.3s ease',
    marginBottom: '10px'
  };

  const warmUpButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: isWarmedUp ? '#27ae60' : '#e74c3c',
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '8px'
  };

  const loadingSpinnerStyle: React.CSSProperties = {
    width: '20px',
    height: '20px',
    border: '3px solid #ffffff',
    borderTop: '3px solid transparent',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  };

  const keyframesStyle = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `;

  const responseStyle: React.CSSProperties = {
    marginTop: '20px',
    padding: '15px',
    backgroundColor: '#f8f9fa',
    borderRadius: '4px',
    whiteSpace: 'pre-wrap'
  };

  const statusStyle: React.CSSProperties = {
    marginBottom: '20px',
    padding: '10px',
    borderRadius: '4px',
    backgroundColor: isWarmedUp ? '#d4edda' : '#f8d7da',
    color: isWarmedUp ? '#155724' : '#721c24',
    textAlign: 'center'
  };

  return (
    <div style={formWrapperStyle}>
      <style>{keyframesStyle}</style>
      <div style={formStyle}>
        <h2 style={{ marginBottom: '20px', textAlign: 'center' }}>Orpheus Dial Out</h2>
        
        <div style={statusStyle}>
          {isWarmedUp ? (
            <div>
              Instance is warmed up! You have {timeLeft} seconds to make the call.
            </div>
          ) : (
            <div>
              Instance needs to be warmed up before making a call.
            </div>
          )}
        </div>

        <button 
          style={warmUpButtonStyle} 
          onClick={handleWarmUp}
          disabled={isWarmedUp || isWarmingUp}
        >
          {isWarmingUp ? (
            <>
              <div style={loadingSpinnerStyle} />
              Warming Up...
            </>
          ) : isWarmedUp ? (
            'Instance Warmed Up'
          ) : (
            'Warm Up Instance'
          )}
        </button>

        {warmUpError && (
          <div style={{ ...responseStyle, backgroundColor: '#fee' }}>
            Warm-up Error: {warmUpError}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div>
            <label style={labelStyle}>
              Phone Number:
              <input
                type="text"
                name="dialoutNumber"
                style={inputStyle}
                value={formData.dialoutNumber}
                onChange={handleInputChange}
                placeholder="Enter phone number (e.g., 18777791003)"
                required
              />
            </label>
          </div>

          <div>
            <label style={labelStyle}>
              Call ID:
              <input
                type="text"
                name="callId"
                style={inputStyle}
                value={formData.callId}
                onChange={handleInputChange}
                placeholder="Enter call ID"
                required
              />
            </label>
          </div>

          <div>
            <label style={labelStyle}>
              Client Name:
              <input
                type="text"
                name="clientName"
                style={inputStyle}
                value={formData.clientName}
                onChange={handleInputChange}
                placeholder="Enter client name"
              />
            </label>
          </div>

          <div>
            <label style={labelStyle}>
              <input
                type="checkbox"
                name="detectVoicemail"
                checked={formData.detectVoicemail}
                onChange={handleInputChange}
                style={{ marginRight: '8px' }}
              />
              Detect Voicemail
            </label>
          </div>

          <button 
            style={buttonStyle} 
            type="submit"
            disabled={!isWarmedUp}
          >
            Make Call
          </button>
        </form>

        {error && (
          <div style={{ ...responseStyle, backgroundColor: '#fee' }}>
            Error: {error}
          </div>
        )}

        {response && (
          <div style={responseStyle}>
            <h3>Response:</h3>
            <pre>{JSON.stringify(response, null, 2)}</pre>
          </div>
        )}
      </div>
    </div>
  );
};

export default CortexDialOut; 